import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import {
  ConverterService,
  GlobalMessageService,
  GlobalMessageType,
  OccEndpointsService,
  OCC_USER_ID_ANONYMOUS
} from '@spartacus/core'
import { Observable, of } from 'rxjs'
import { catchError, pluck, tap } from 'rxjs/operators'
import { EbOrderEntry } from '../../../cart/interface/eb-order-entry.interface'
import { EbFavorites } from '../../interfaces/eb-favorites.interface'
import { EB_FAVORITES_NORMALIZER } from '../converters/converters'

enum FavoritesAction {
  GET_ALL = 'get.all',
  GET_ALL_FOR_PRODUCT = 'get.all.for.product',
  GET_SINGLE = 'get.single',
  CREATE = 'create',
  REMOVE = 'remove',
  RENAME = 'rename',
  COMMENT = 'comment',
  SHARE = 'share',
  ENTRIES_REMOVE_SELECTED = 'entries.remove.selected',
  ENTRIES_REMOVE_SINGLE = 'entries.remove.single',
  PRODUCT_REMOVE = 'product.remove',
  PRODUCT_ADD = 'product.add'
}

type FavoritesParameters = {
  userId: string
  listId?: string
  name?: string
  entryNumber?: string
  productCode?: string
  isShared?: boolean
}

const queryParamsMap = {
  'get.all.for.product': ['productCode'],
  create: ['name', 'productCode']
}

@Injectable()
export class EbFavoritesAdapter {
  constructor(
    private readonly http: HttpClient,
    private readonly occEndpointsService: OccEndpointsService,
    private readonly converterService: ConverterService,
    protected globalMessageService: GlobalMessageService
  ) {}

  private static toCamelCaseString(str: string): string {
    return str.replace(/\W+(\w?)/g, (a, b) => b.toUpperCase())
  }

  private static createDeleteEntries(entriesToRemove: EbOrderEntry[]): EbOrderEntry[] {
    if (entriesToRemove.length > 0) {
      return entriesToRemove.map(value => {
        return {
          product: {
            code: value.product.code
          }
        } as EbOrderEntry
      })
    } else {
      return []
    }
  }

  getLists(userId): Observable<EbFavorites[]> {
    if (userId === OCC_USER_ID_ANONYMOUS) {
      return of([])
    } else {
      return this.getListsCommon(FavoritesAction.GET_ALL, { userId })
    }
  }

  getListsForProduct(userId, productCode: string = null): Observable<EbFavorites[]> {
    if (userId === OCC_USER_ID_ANONYMOUS) {
      return of([])
    } else {
      return this.getListsCommon(FavoritesAction.GET_ALL_FOR_PRODUCT, { userId, productCode })
    }
  }

  private getListsCommon(action: FavoritesAction, params) {
    const url = this.getUrlForAction(action, params)
    return this.handleResponse(action, this.http.get<{ carts: EbFavorites[] }>(url), {}, false)
  }

  getDetails(userId, listId): Observable<EbFavorites> {
    if (userId === OCC_USER_ID_ANONYMOUS) {
      return of(null)
    } else {
      const url = this.getUrlForAction(FavoritesAction.GET_SINGLE, {
        listId,
        userId
      })
      return this.handleResponse(
        FavoritesAction.GET_ALL,
        this.http.get<{ cart: EbFavorites }>(url),
        {},
        false,
        false
      ).pipe(this.converterService.pipeable(EB_FAVORITES_NORMALIZER))
    }
  }

  createFavorites(userId, productCode, name: string): Observable<EbFavorites[]> {
    if (userId && userId !== OCC_USER_ID_ANONYMOUS) {
      const url = this.getUrlForAction(FavoritesAction.CREATE, {
        userId,
        productCode
      })
      return this.handleResponse(
        FavoritesAction.CREATE,
        this.http.post(url, null, {
          params: {
            listName: name
          }
        }),
        {
          name
        }
      ) as Observable<EbFavorites[]>
    }
  }

  removeFavorites(userId: string, favorites: EbFavorites): Observable<EbFavorites[]> {
    if (userId && userId !== OCC_USER_ID_ANONYMOUS) {
      const url = this.getUrlForAction(FavoritesAction.REMOVE, { userId, listId: favorites.code })
      return this.handleResponse(
        FavoritesAction.REMOVE,
        this.http.delete(url),
        favorites
      ) as Observable<EbFavorites[]>
    }
  }

  removeEntryFromFavorites(
    userId: string,
    favorites: EbFavorites,
    entry: EbOrderEntry
  ): Observable<EbFavorites> {
    if (userId && userId !== OCC_USER_ID_ANONYMOUS) {
      const url = this.getUrlForAction(FavoritesAction.ENTRIES_REMOVE_SELECTED, {
        userId,
        listId: favorites.code
      })
      return this.handleResponse(
        FavoritesAction.ENTRIES_REMOVE_SINGLE,
        this.http.delete(url, {
          body: {
            cartId: favorites.code,
            orderEntries: EbFavoritesAdapter.createDeleteEntries([entry])
          }
        }),
        favorites,
        true,
        false
      ).pipe(this.converterService.pipeable(EB_FAVORITES_NORMALIZER))
    }
  }

  removeProductFromFavorites(
    userId: string,
    favorites: EbFavorites,
    productCode: string
  ): Observable<EbFavorites[]> {
    if (userId && userId !== OCC_USER_ID_ANONYMOUS) {
      const url = this.getUrlForAction(FavoritesAction.PRODUCT_REMOVE, {
        userId
      })
      return this.handleResponse(
        FavoritesAction.PRODUCT_REMOVE,
        this.http.delete(url, {
          body: {
            listIds: [favorites.code],
            productCode
          }
        }),
        favorites
      ) as Observable<EbFavorites[]>
    }
  }

  removeSelectedEntriesFromFavorites(
    userId: string,
    favorites: EbFavorites,
    entriesToRemove: EbOrderEntry[] = []
  ): Observable<EbFavorites> {
    if (userId && userId !== OCC_USER_ID_ANONYMOUS && entriesToRemove.length) {
      const url = this.getUrlForAction(FavoritesAction.ENTRIES_REMOVE_SELECTED, {
        userId,
        listId: favorites.code
      })
      return this.handleResponse(
        FavoritesAction.ENTRIES_REMOVE_SELECTED,
        this.http.delete(url, {
          body: {
            cartId: favorites.code,
            orderEntries: EbFavoritesAdapter.createDeleteEntries(entriesToRemove)
          }
        }),
        favorites,
        true,
        false
      ).pipe(this.converterService.pipeable(EB_FAVORITES_NORMALIZER))
    }
  }

  addProductToFavorites(userId: string, productCode: string, favorites: EbFavorites) {
    if (userId && userId !== OCC_USER_ID_ANONYMOUS && productCode && favorites?.code) {
      const url = this.getUrlForAction(FavoritesAction.PRODUCT_ADD, {
        userId
      })
      const response$ = this.http.post(url, {
        productCode,
        listIds: [favorites.code]
      })
      return this.handleResponse(FavoritesAction.PRODUCT_ADD, response$, favorites) as Observable<
        EbFavorites[]
      >
    }
  }

  toggleSharedStatusOfFavorites(
    userId: string,
    favorites: EbFavorites,
    isShared: boolean
  ): Observable<EbFavorites> {
    if (userId && userId !== OCC_USER_ID_ANONYMOUS && favorites?.code) {
      const url = this.getUrlForAction(FavoritesAction.SHARE, {
        userId,
        listId: favorites.code,
        isShared
      })
      return this.handleResponse(
        FavoritesAction.SHARE,
        this.http.post(url, {}),
        favorites,
        true,
        false
      ).pipe(this.converterService.pipeable(EB_FAVORITES_NORMALIZER))
    }
  }

  updateFavoritesName(
    userId: string,
    favorites: EbFavorites,
    listName: string
  ): Observable<EbFavorites> {
    if (userId && userId !== OCC_USER_ID_ANONYMOUS && favorites?.code) {
      const url = this.getUrlForAction(FavoritesAction.RENAME, {
        userId,
        listId: favorites.code
      })
      return this.handleResponse(
        FavoritesAction.RENAME,
        this.http.post(url, null, {
          params: {
            listName
          }
        }),
        {
          name: listName
        },
        true,
        false
      ).pipe(this.converterService.pipeable(EB_FAVORITES_NORMALIZER))
    }
  }

  updateCommentOnEntry(
    item: { entryNumber?: number; comment?: string },
    list: EbFavorites,
    userId: string
  ): Observable<EbFavorites> {
    if (userId && userId !== OCC_USER_ID_ANONYMOUS && list.code) {
      const url = this.getUrlForAction(FavoritesAction.COMMENT, {
        userId
      })
      return this.handleResponse(
        FavoritesAction.COMMENT,
        this.http.post(url, {
          cartId: list.code,
          orderEntries: [item]
        }),
        list,
        true,
        false
      ).pipe(this.converterService.pipeable(EB_FAVORITES_NORMALIZER))
    }
  }

  private showSuccessMessage(action: FavoritesAction, messageParams: any = {}) {
    this.showMessage(action, false, messageParams)
  }

  private showErrorMessage(action: FavoritesAction, messageParams: any = {}) {
    this.showMessage(action, true, messageParams)
  }

  private showMessage(action: FavoritesAction, error = false, messageParams: any = {}) {
    const key = `favorites.${action}.${error ? 'error' : 'success'}`
    const messageType = error
      ? GlobalMessageType.MSG_TYPE_ERROR
      : GlobalMessageType.MSG_TYPE_CONFIRMATION
    this.globalMessageService.add({ key, params: messageParams }, messageType)
  }

  private getUrlForAction(action: FavoritesAction, params: FavoritesParameters): string {
    const endpoint = EbFavoritesAdapter.toCamelCaseString(
      action.toString() === 'create' && params.productCode
        ? 'favorites.CreateForProduct'
        : `favorites.${action}`
    )
    const queryParams = this.getQueryParamsForAction(action, params)
    return this.occEndpointsService.buildUrl(endpoint, { urlParams: params, queryParams })
  }

  private getQueryParamsForAction(
    action: FavoritesAction,
    urlParams: FavoritesParameters
  ): Partial<FavoritesParameters> {
    const allowedKeys = queryParamsMap[action] || []
    return allowedKeys.reduce((queryParams, key) => {
      if (urlParams[key]) {
        queryParams[key] = urlParams[key]
      }
      return queryParams
    }, {})
  }

  private handleResponse(
    action: FavoritesAction,
    result: Observable<any>,
    favorites: EbFavorites = {},
    notifySuccess = true,
    convertList = true
  ): Observable<any> {
    const stream = result.pipe(
      catchError(err => {
        if (
          err?.error?.errors[0]?.type === 'EBFavoritesIllegalStateError' ||
          err?.error?.errors[0]?.type === 'EBFavoritesError'
        ) {
          this.globalMessageService.add(
            err?.error?.errors[0]?.message,
            GlobalMessageType.MSG_TYPE_ERROR
          )
        } else {
          this.showErrorMessage(action)
        }
        return err
      }),
      tap(() => notifySuccess && this.showSuccessMessage(action, favorites))
    )
    if (convertList) {
      return stream.pipe(
        pluck('carts'),
        this.converterService.pipeableMany(EB_FAVORITES_NORMALIZER)
      )
    } else {
      return stream
    }
  }
}
